/**
 * customerApi.ts - Customer-facing API service with caching
 * 
 * Performance Optimizations:
 * - In-memory cache for vendor and menu data (5min TTL)
 * - Smart request deduplication
 * - Error retry logic
 */

import {
  buildEstimatedReadyAt,
  clampEstimatedMinutes,
  DEFAULT_SEAT_ETA_MINUTES,
} from '../utils/orderDeliveryEta';

// Configure API base URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

// Structured API Error
export class ApiError extends Error {
  status: number;
  code?: string;
  details?: any;
  constructor(message: string, status: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
type CacheEntry<T> = { data: T; timestamp: number };
const cache = new Map<string, CacheEntry<any>>();

// Revalidation callbacks for SWR pattern
type RevalidationCallback<T> = (data: T) => void;
const revalidationCallbacks = new Map<string, Set<RevalidationCallback<any>>>();

// Request deduplication
const pendingRequests = new Map<string, Promise<any>>();

// Helper function to get from cache
function getFromCache<T>(key: string): T | null {
  const entry = cache.get(key);
  if (entry) {
    const isExpired = Date.now() - entry.timestamp > CACHE_TTL;
    if (!isExpired) {
      return entry.data as T;
    }
    cache.delete(key);
  }
  return null;
}

// Helper function to set cache and notify listeners
function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
  
  // Notify all registered callbacks for this key
  const callbacks = revalidationCallbacks.get(key);
  if (callbacks) {
    callbacks.forEach(cb => cb(data));
  }
}

// Subscribe to revalidation updates
export function subscribeToRevalidation<T>(key: string, callback: RevalidationCallback<T>): () => void {
  if (!revalidationCallbacks.has(key)) {
    revalidationCallbacks.set(key, new Set());
  }
  revalidationCallbacks.get(key)!.add(callback);
  
  // Return unsubscribe function
  return () => {
    const callbacks = revalidationCallbacks.get(key);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        revalidationCallbacks.delete(key);
      }
    }
  };
}

// Retry logic for failed requests
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 2
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      // Exponential backoff: 100ms, 200ms, etc.
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, (i + 1) * 100));
      }
    }
  }
  
  throw lastError;
}

// Helper function for API calls with caching and optional revalidation
async function apiCall<T>(
  endpoint: string,
  options?: RequestInit,
  revalidate: boolean = false
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  // Only cache GET requests
  if (!options || options.method === 'GET') {
    const cachedData = getFromCache<T>(url);
    
    // Return cached data immediately for SWR pattern
    if (cachedData && !revalidate) {
      // Trigger background revalidation if cache is older than 30 seconds
      const cacheEntry = cache.get(url);
      if (cacheEntry && Date.now() - cacheEntry.timestamp > 30000) {
        // Fire and forget background refresh
        apiCall<T>(endpoint, options, true).catch(() => {/* ignore errors */});
      }
      return cachedData;
    }

    // Check if request is already in flight
    if (pendingRequests.has(url)) {
      return pendingRequests.get(url)!;
    }
  }

  const request = withRetry(async () => {
    let response: Response;
    try {
      response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      }
      });
    } catch (err: any) {
      // Network error
      throw new ApiError('Network error. Please check your connection.', 0, 'NETWORK_ERROR');
    }

    if (!response.ok) {
      let payload: any = null;
      try {
        payload = await response.json();
      } catch {}
      const message = payload?.message || `API Error: ${response.status} ${response.statusText}`;
      const code = payload?.code || undefined;
      throw new ApiError(message, response.status, code, payload);
    }

    return response.json() as Promise<T>;
  });

  // Cache the request for deduplication
  if (!options || options.method === 'GET') {
    pendingRequests.set(url, request);
  }

  try {
    const data = await request;
    
    // Cache successful GET responses
    if (!options || options.method === 'GET') {
      setCache(url, data);
    }
    
    return data;
  } finally {
    pendingRequests.delete(url);
  }
}

function toBool(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function normalizeModifierOption(raw: Record<string, unknown>) {
  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    priceAdjustment: Number(raw.priceAdjustment ?? 0),
    isDefault: toBool(raw.isDefault),
    available: raw.available === undefined ? true : toBool(raw.available),
  };
}

function normalizeModifier(raw: Record<string, unknown>) {
  const options = Array.isArray(raw.options)
    ? raw.options.map((opt) => normalizeModifierOption(opt as Record<string, unknown>))
    : [];

  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    type: (raw.type === 'required' ? 'required' : 'optional') as 'required' | 'optional',
    minSelections: Number(raw.minSelections ?? 0),
    maxSelections: Number(raw.maxSelections ?? 1),
    options,
  };
}

function normalizeMenuItem(raw: Record<string, unknown>): MenuItem {
  const modifiers = Array.isArray(raw.modifiers)
    ? raw.modifiers.map((mod) => normalizeModifier(mod as Record<string, unknown>))
    : undefined;

  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    price: Number(raw.price ?? 0),
    category: String(raw.category ?? ''),
    image: raw.image ? String(raw.image) : undefined,
    available: raw.available === undefined ? true : toBool(raw.available),
    featured: toBool(raw.featured),
    is_promo: toBool(raw.is_promo),
    promo_price: raw.promo_price != null ? Number(raw.promo_price) : null,
    allergens: Array.isArray(raw.allergens) ? raw.allergens.map(String) : undefined,
    dietaryTags: Array.isArray(raw.dietaryTags) ? raw.dietaryTags.map(String) : undefined,
    modifiers,
  };
}

function normalizeVendor(raw: Record<string, unknown>): Vendor {
  const menuItems = Array.isArray(raw.menuItems)
    ? raw.menuItems.map((item) => normalizeMenuItem(item as Record<string, unknown>))
    : [];

  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    description: raw.description ? String(raw.description) : undefined,
    image: raw.image ? String(raw.image) : undefined,
    sections: Array.isArray(raw.sections) ? raw.sections.map(String) : [],
    cuisine_type: raw.cuisine_type != null ? String(raw.cuisine_type) : null,
    rating: raw.rating != null ? Number(raw.rating) : undefined,
    currentWaitMinutes: raw.currentWaitMinutes != null ? Number(raw.currentWaitMinutes) : null,
    menuItems,
    contactInfo: raw.contactInfo as Vendor['contactInfo'],
  };
}

// Types
export interface ModifierOption {
  id: string;
  name: string;
  priceAdjustment: number;
  isDefault: boolean;
  available: boolean;
}

export interface Modifier {
  id: string;
  name: string;
  type: 'required' | 'optional';
  minSelections: number;
  maxSelections: number;
  options: ModifierOption[];
}

export interface SelectedOption {
  modifierId: string;
  optionIds: string[];
}

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image?: string;
  available: boolean;
  featured?: boolean;
  is_promo?: boolean;
  promo_price?: number | null;
  allergens?: string[];
  dietaryTags?: string[];
  modifiers?: Modifier[];
}

export interface Vendor {
  id: string;
  name: string;
  description?: string;
  image?: string;
  sections: string[];
  cuisine_type?: string | null;
  rating?: number;
  currentWaitMinutes?: number | null;
  menuItems: MenuItem[];
  contactInfo?: {
    phone?: string;
    email?: string;
  };
}

export interface OrderItem {
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  customizations?: string[];
  selectedOptions?: SelectedOption[];
  note?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  vendorId: string;
  vendorName: string;
  customerName: string;
  customerId: string;
  items: OrderItem[];
  totalAmount: number;
  seatLocation: {
    section: string;
    row: string;
    seat: string;
  };
  deliveryInstructions?: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'out-for-delivery' | 'delivered' | 'cancelled';
  placedAt: string;
  completedAt?: string;
  estimatedReadyAt?: string;
}

function normalizeOrder(raw: Record<string, unknown>): Order {
  const seatLocation = raw.seatLocation as Order['seatLocation'] | undefined;
  const section = seatLocation?.section || String(raw.seatSection || '');
  const row = seatLocation?.row || String(raw.seatRow || '');
  const seat = seatLocation?.seat || String(raw.seatNumber || '');

  const createdAt = String(raw.placedAt || raw.createdAt || '');
  let estimatedReadyAt = raw.estimatedReadyAt ? String(raw.estimatedReadyAt) : undefined;
  if (!estimatedReadyAt && createdAt) {
    const minutes =
      raw.estimatedTime != null
        ? clampEstimatedMinutes(Number(raw.estimatedTime))
        : DEFAULT_SEAT_ETA_MINUTES;
    const built = buildEstimatedReadyAt(createdAt, minutes);
    if (built) estimatedReadyAt = built;
  }

  const items = Array.isArray(raw.items)
    ? raw.items.map((item) => {
        const itemRow = item as Record<string, unknown>;
        return {
          menuItemId: String(itemRow.menuItemId || itemRow.id || ''),
          name: String(itemRow.name || ''),
          price: Number(itemRow.price) || 0,
          quantity: Number(itemRow.quantity) || 0,
          customizations: itemRow.customizations as string[] | undefined,
        };
      })
    : [];

  return {
    id: String(raw.id),
    orderNumber: String(raw.orderNumber || ''),
    vendorId: String(raw.vendorId || ''),
    vendorName: String(raw.vendorName || ''),
    customerName: String(raw.customerName || ''),
    customerId: String(raw.customerId || ''),
    items,
    totalAmount: Number(raw.totalAmount) || 0,
    seatLocation: { section, row, seat },
    deliveryInstructions: raw.deliveryInstructions
      ? String(raw.deliveryInstructions)
      : raw.notes
      ? String(raw.notes)
      : undefined,
    status: raw.status as Order['status'],
    placedAt: String(raw.placedAt || raw.createdAt || ''),
    completedAt: raw.completedAt ? String(raw.completedAt) : undefined,
    estimatedReadyAt,
  };
}

export interface CreateOrderRequest {
  vendorId: string;
  vendorName: string;
  customerName: string;
  customerId: string;
  items: OrderItem[];
  totalAmount: number;
  seatLocation: {
    section: string;
    row: string;
    seat: string;
  };
  deliveryInstructions?: string;
}

export interface PaymentIntent {
  clientSecret: string;
  amount: number;
  currency: string;
}

// Vendor APIs
export const vendorService = {
  /**
   * Get all vendors (with SWR support)
   */
  async getAllVendors(): Promise<Vendor[]> {
    try {
      const data = await apiCall<Record<string, unknown>[]>('/vendors');
      return data.map(normalizeVendor);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  },

  /**
   * Get single vendor with full menu (with SWR support)
   */
  async getVendor(vendorId: string, options?: { forceRefresh?: boolean }): Promise<Vendor> {
    try {
      const data = await apiCall<Record<string, unknown>>(
        `/vendors/${vendorId}`,
        undefined,
        options?.forceRefresh ?? false
      );
      return normalizeVendor(data);
    } catch (error) {
      console.error(`Error fetching vendor ${vendorId}:`, error);
      throw error;
    }
  },

  /**
   * Subscribe to vendor list updates
   */
  subscribeToVendors(callback: RevalidationCallback<Vendor[]>): () => void {
    return subscribeToRevalidation(`${API_BASE}/vendors`, (data) => {
      const vendors = Array.isArray(data)
        ? data.map((vendor) => normalizeVendor(vendor as Record<string, unknown>))
        : [];
      callback(vendors);
    });
  },

  /**
   * Subscribe to specific vendor updates
   */
  subscribeToVendor(vendorId: string, callback: RevalidationCallback<Vendor>): () => void {
    return subscribeToRevalidation(`${API_BASE}/vendors/${vendorId}`, (data) => {
      callback(normalizeVendor(data as Record<string, unknown>));
    });
  }
};

// Order APIs
export const orderService = {
  /**
   * Create a new order
   */
  async createOrder(orderData: CreateOrderRequest): Promise<Order> {
    try {
      const data = await apiCall<Record<string, unknown>>('/orders', {
        method: 'POST',
        body: JSON.stringify(orderData)
      });
      return normalizeOrder(data);
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  /**
   * Get order by ID
   */
  async getOrder(orderId: string): Promise<Order> {
    try {
      const token = localStorage.getItem('authToken');
      const data = await apiCall<Record<string, unknown>>(`/orders/${orderId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return normalizeOrder(data);
    } catch (error) {
      console.error(`Error fetching order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get the authenticated customer's own orders via JWT
   */
  async getCustomerOrders(): Promise<Order[]> {
    try {
      const token = localStorage.getItem('authToken');
      const data = await apiCall<Record<string, unknown>[]>('/orders/my', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      return data.map(normalizeOrder);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw error;
    }
  },

  /**
   * Update order status
   */
  async updateOrderStatus(orderId: string, status: Order['status']): Promise<Order> {
    try {
      return await apiCall<Order>(`/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
    } catch (error) {
      console.error(`Error updating order ${orderId} status:`, error);
      throw error;
    }
  }
};

// Payment APIs (Stripe)
export const paymentService = {
  /**
   * Create a payment intent for Stripe
   * 
   * @param amount Amount in cents (e.g., $50 = 5000)
   * @param orderId Order ID for tracking
   */
  async createPaymentIntent(amount: number, orderId: string, email: string): Promise<PaymentIntent> {
    try {
      return await apiCall<PaymentIntent>('/payments/create-intent', {
        method: 'POST',
        body: JSON.stringify({ amount, orderId, email })
      });
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  },

  /**
   * Confirm payment
   */
  async confirmPayment(paymentIntentId: string, orderId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiCall<{ success: boolean; message: string }>('/payments/confirm', {
        method: 'POST',
        body: JSON.stringify({ paymentIntentId, orderId })
      });
    } catch (error) {
      console.error('Error confirming payment:', error);
      throw error;
    }
  }
};

// SMS Authentication APIs
export const authService = {
  /**
   * Send verification code to phone number
   */
  async sendVerificationCode(phoneNumber: string): Promise<{ success: boolean; expiresIn: number; bypassVerification?: boolean }> {
    try {
      return await apiCall<{ success: boolean; expiresIn: number; bypassVerification?: boolean }>('/auth/send-code', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber })
      });
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  },

  /**
   * Verify OTP code and authenticate user
   */
  async verifyCode(phoneNumber: string, code: string): Promise<{ success: boolean; token: string; user?: { id: string; phoneNumber: string; name?: string; section?: string; row?: string; seat?: string } }> {
    try {
      return await apiCall<{ success: boolean; token: string; user?: { id: string; phoneNumber: string; name?: string; section?: string } }>('/auth/verify-code', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber, code })
      });
    } catch (error) {
      console.error('Error verifying code:', error);
      throw error;
    }
  },

  /**
   * Resend verification code
   */
  async resendCode(phoneNumber: string): Promise<{ success: boolean; expiresIn: number }> {
    try {
      return await apiCall<{ success: boolean; expiresIn: number }>('/auth/resend-code', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber })
      });
    } catch (error) {
      console.error('Error resending code:', error);
      throw error;
    }
  },

  /**
   * Update authenticated customer's profile (name + section)
   */
  async updateProfile(name: string, section: string, row: string, seat: string): Promise<{ success: boolean; user: { id: string; name: string; section: string; row: string; seat: string } }> {
    const token = localStorage.getItem('authToken');
    return await apiCall<{ success: boolean; user: { id: string; name: string; section: string; row: string; seat: string } }>('/auth/profile', {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name, section, row, seat })
    });
  },

  /**
   * Change authenticated customer's phone number (requires OTP for new number)
   */
  async changePhone(
    newPhoneNumber: string,
    code: string
  ): Promise<{ success: boolean; user: { id: string; phoneNumber: string; phone?: string; name?: string; section?: string; row?: string; seat?: string } }> {
    const token = localStorage.getItem('authToken');
    return await apiCall<{ success: boolean; user: { id: string; phoneNumber: string; phone?: string; name?: string; section?: string; row?: string; seat?: string } }>(
      '/auth/change-phone',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ newPhoneNumber, code }),
      }
    );
  }
};

const customerApi = {
  vendorService,
  orderService,
  paymentService,
  authService
};

export default customerApi;
